/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetServerSideProps } from 'next';

import { PageRendererComponent, getProcessEnvs, prePageCall, logger } from '@marriott/mi-headless-utils';
import {
  fetchCFData,
  buildPatternSlots,
  mergeCfAndDcaToSlots,
  resolveRegionFromEdgescape,
} from '@marriott/mi-homepage-components';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';
import { envVarKeys, POLARIS_AB_HEADER, AKAMAI_EDGESCAPE_HEADER } from '../_constants';
import operationSignatures from '../_constants/operationSignatures.json';
import homepageMockData from '../mock/polaris-logged-out.json';

const { log } = logger({})('page.tsx');

export default function pageRenderer(props: any) {
  return <PageRendererComponent {...props} />;
}

/**
 * Read the geo-targeting feature flag for the Homepage. Centralized so the SSR
 * data function and the post-AEM model step evaluate the flag the same way.
 */
const getGeoTargetingFlag = async () => {
  const flag = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_HOMEPAGE_GEO_TARGETING, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  const isEnabled = flag?.[FEATURE_FLAGS.EXPERIENCE_MCOM_HOMEPAGE_GEO_TARGETING] === true;
  log.debug(`[GEO-TARGETING][SSR][getGeoTargetingFlag] rawFlag: ${JSON.stringify(flag)}, isEnabled: ${isEnabled}`);
  return { flag, isEnabled };
};

const homepageSpecificData = async (context?: { [key: string]: any }, parallelCallsResponse?: any) => {
  const {
    NEXT_PUBLIC_SUBMIT_SEARCH_URL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY = null,
    UXL_IMAGE_CACHE_DOMAIN = null,
    SESSION_GET_CALL_URL_CLIENT,
    SESSION_APP_GET_CALL_V1_URL,
    DEPLOYED_ENV_CONFIG,
    TARGET_PROPERTY_TOKEN,
    NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    SESSION_APP_POST_CALL_URL,
    INJECT_BRAND_CSS,
    NEXT_PUBLIC_PREFIX,
    GET_DESTINATION_TYPE_FLAG,
    SET_FALLBACK_TO_CONVOAI,
  } = getProcessEnvs();

  // Feature flag call for logo
  const featureFlagLogo = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  // Feature flag call to get cardCarousel flag
  const featureFlags = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_CAROUSEL_LIGHTINING_VARIANTS, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  // Feature flag to control deferred hydration optimization
  const featureFlagDeferHydration = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_HOMEPAGE_DEFER_HYDRATION, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  const featureFlagLegacyNav = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LEGACY_NAV, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
  });
  //Fetch concurFlow feature flag
  const featureFlagEnableConcurFlow = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ENABLE_CONCUR_FLOW, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
    enableStream: true,
  });
  // Feature flag that gates the Homepage geo-targeting (geographically relevant content) feature.
  const { flag: featureFlagGeoTargeting } = await getGeoTargetingFlag();
  const polarisHeader = context?.req?.headers?.[POLARIS_AB_HEADER];
  const isPolarisVariant = polarisHeader !== undefined && polarisHeader !== null && polarisHeader !== '';
  const isAuthenticated = parallelCallsResponse?.sessionData?.authenticated ?? false;

  return {
    ...(isPolarisVariant && {
      EXPERIENCE_SEGMENT: [isAuthenticated ? 'Logged In' : 'NOT Logged In', 'Polaris'],
    }),
    operationSignatures,
    productSpecificResponse: [],
    NEXT_PUBLIC_SUBMIT_SEARCH_URL: NEXT_PUBLIC_SUBMIT_SEARCH_URL ?? null,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL: NEXT_PUBLIC_AVAILABILITY_SEARCH_URL ?? null,
    // Next.js cannot serialize `undefined` in props; ensure we pass null when unset.
    NEXT_PUBLIC_FETCH_OFFERS_API_URL: NEXT_PUBLIC_FETCH_OFFERS_API_URL ?? null,
    TARGET_PROPERTY_TOKEN: TARGET_PROPERTY_TOKEN ?? null,
    GOOGLE_MAP_API_KEY: GOOGLE_MAP_API_KEY ?? null,
    BAIDU_MAP_API_KEY,
    UXL_IMAGE_CACHE_DOMAIN,
    SESSION_GET_CALL_URL_CLIENT: SESSION_GET_CALL_URL_CLIENT ?? null,
    SESSION_APP_GET_CALL_V1_URL: SESSION_APP_GET_CALL_V1_URL ?? null,
    SESSION_APP_POST_CALL_URL: SESSION_APP_POST_CALL_URL ?? null,
    DEPLOYED_ENV_CONFIG: DEPLOYED_ENV_CONFIG ?? null,
    INJECT_BRAND_CSS: INJECT_BRAND_CSS ?? null,
    buildInfo: {
      deployedEnvType: process.env.DEPLOYED_ENV_TYPE ?? null,
      deployedEnvConfig: process.env.DEPLOYED_ENV_CONFIG ?? null,
      jenkinsDeploymentBuild: process.env.JENKINS_DEPLOYMENT_BUILD ?? null,
    },
    featureFlagLogo: featureFlagLogo ?? null,
    featureFlagDeferHydration: featureFlagDeferHydration ?? null,
    NEXT_PUBLIC_PREFIX: NEXT_PUBLIC_PREFIX ?? null,
    featureFlags: featureFlags ?? null,
    nextPublicEnvKeys: [...Object.keys(envVarKeys)],
    GET_DESTINATION_TYPE_FLAG: GET_DESTINATION_TYPE_FLAG ?? null,
    featureFlagLegacyNav: featureFlagLegacyNav ?? null,
    featureFlagEnableConcurFlow: featureFlagEnableConcurFlow ?? null,
    featureFlagGeoTargeting: featureFlagGeoTargeting ?? null,
    SET_FALLBACK_TO_CONVOAI: SET_FALLBACK_TO_CONVOAI ?? null,
  };
};

/**
 * Ensure SSR props are JSON-safe for Next.js by replacing nested `undefined`
 * values with `null`.
 */
const sanitizeForSsr = <T,>(value: T): T =>
  JSON.parse(JSON.stringify(value, (_key, currentValue) => (currentValue === undefined ? null : currentValue))) as T;

/**
 * This function is called on the server side before the page is rendered.
 * @param _context - The context object containing the request and response objects.
 * @param _parallelCallsResponse - The parallel calls response object.
 * @param _interceptorResponse - The interceptor response object.
 * @param model - The model object containing the page data.
 * @returns The updated model object.
 */
const postAEMModelOperations = async (
  context: any,
  _parallelCallsResponse: any,
  _interceptorResponse: any,
  model: any
) => {
  try {
    log.debug('Server Side: postAEMModelOperations started');
    const gridItems = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'] || {};

    const tl = gridItems?.tabbedlayout || gridItems?.dynamictabbedlayout;
    const isNorthStarPage = !!tl && (typeof tl === 'object' ? true : !!tl);
    model.isNorthStarPage = isNorthStarPage;

    const polarisHeader = context?.req?.headers?.[POLARIS_AB_HEADER];
    const isPolarisVariant = polarisHeader !== undefined && polarisHeader !== null && polarisHeader !== '';
    model.pageVariant = isPolarisVariant ? 'polaris' : 'bau';

    const dynamictabbedlayoutComp = gridItems['dynamictabbedlayout'];
    if (dynamictabbedlayoutComp) {
      const hasNgSearchForm = Object.values(gridItems).some(
        (comp: any) => typeof comp?.[':type'] === 'string' && comp[':type'].includes('ngsearchform')
      );
      dynamictabbedlayoutComp.isNextGenSearchForm = hasNgSearchForm;

      // Detect whether ConvoAI search is enabled. The `enableConvoAISearch` flag lives on the
      // search form component, which can be either an `ngsearchform` or a `baselinesearchform`,
      // and is nested inside a dcaabtestwrapper/container (not a direct grid item). So we walk
      // the model, find the search form node by its `:type`, and read its `enableConvoAISearch`.
      // The DTL uses this to reserve extra spacing below the tab title so the ConvoAI search
      // form does not collide with the DTL content.
      const isSearchFormType = (type: unknown): boolean =>
        typeof type === 'string' && (type.includes('ngsearchform') || type.includes('baselinesearchform'));
      const hasConvoAISearchEnabled = (node: any): boolean => {
        if (!node || typeof node !== 'object') return false;
        if (isSearchFormType(node[':type']) && node.enableConvoAISearch === true) return true;
        return Object.values(node).some(child => hasConvoAISearchEnabled(child));
      };
      dynamictabbedlayoutComp.enableConvoAISearch = hasConvoAISearchEnabled(gridItems);

      const tabDetails: any[] = dynamictabbedlayoutComp.tabDetails || [];
      if (!tabDetails.length) return model;

      // Geo-targeting - gated by feature flag. SSR resolves its OWN region from
      // the Akamai edgescape header + regionStatesMapping (authored in
      // page.model.json) and uses it only for the initial CF fetch below.
      // The region is intentionally NOT persisted to the client model — CSR
      // performs its own independent lookup from the DataLayer.
      // When the flag is off (or no mapping exists) `region` stays undefined and
      // behavior is unchanged (default, non-regional content).
      //
      // For geo-cf reserved slots the region must NOT be baked into the cached
      // (logged-out) HTML — a cache hit would leak one region to everyone. So we
      // only resolve/apply a region on the SSR CF fetch for LOGGED-IN (uncached)
      // requests; logged-out requests always fetch the `master` copy, seed it as
      // the geo-cf fallback, and let the client fill the region-specific content.
      const isAuthenticated: boolean = _parallelCallsResponse?.sessionData?.authenticated === true;
      let region: string | undefined;
      const { isEnabled: isGeoTargetingEnabled } = await getGeoTargetingFlag();
      log.debug(
        `[GEO-TARGETING][SSR][postAEMModelOperations] isGeoTargetingEnabled: ${isGeoTargetingEnabled}, isAuthenticated: ${isAuthenticated}`
      );
      if (isGeoTargetingEnabled && isAuthenticated) {
        const regionStatesMapping = model?.regionStatesMapping;
        const edgescapeHeader = context?.req?.headers?.[AKAMAI_EDGESCAPE_HEADER];
        log.debug(
          `[GEO-TARGETING][SSR][postAEMModelOperations] edgescapeHeader: ${JSON.stringify(
            edgescapeHeader
          )}, regionStatesMapping: ${JSON.stringify(regionStatesMapping)}`
        );
        region = resolveRegionFromEdgescape(edgescapeHeader, regionStatesMapping);
        log.debug(`[GEO-TARGETING][SSR][postAEMModelOperations] resolved region: ${region}`);
      }

      const queryTab: string | undefined = context?.query?.tab as string;
      const chosenTab = tabDetails.find(t => (t.tabName || '').toLowerCase() === (queryTab || '').toLowerCase());
      const tabToRender = chosenTab ?? tabDetails[0];
      const bannerFirst = Boolean(tabToRender.bannerFirst);
      const squareFirst = tabToRender.squareFirst !== false;
      const dcaTiles = tabToRender.DCAtiles || tabToRender.dcaTilesList || [];
      const geoTiles = isGeoTargetingEnabled ? tabToRender.geoContentTilesList || [] : [];
      const slotsMeta = buildPatternSlots(bannerFirst, squareFirst, dcaTiles, geoTiles);
      // Logged-out geo-cf slots seed the `master` copy (region undefined here) and
      // show a skeleton until the client fills them; logged-in seeds the region copy.
      const cf = await fetchCFData(tabToRender.tabName, false, region);
      const mergedSlots = mergeCfAndDcaToSlots(slotsMeta, dcaTiles, cf, { geoNodes: geoTiles, isAuthenticated });

      dynamictabbedlayoutComp.requestedTabData = sanitizeForSsr(mergedSlots);
      dynamictabbedlayoutComp.requestedTab = tabToRender.tabName;
    }
  } catch (err) {
    // Silent failure: never block rendering on SSR tab resolution. Log for observability.
    log.error(`[GEO-TARGETING][SSR][postAEMModelOperations] error: ${err instanceof Error ? err.message : err}`);
  }
  return model;
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
export const getServerSideProps: GetServerSideProps<any> = prePageCall(
  homepageSpecificData,
  homepageMockData || {},
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);
